import { ComponentProps, useMemo } from 'react'
import { IconExclamationCircle } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import { PostalCodeTypes, useQueryDeliverySlots } from 'modules/delivery'
import { RadioItem } from 'shared/ui/RadioGroup'
import { Tooltip } from 'shared/ui/Tooltip'
import { cssColors } from 'shared/ui/utils/cssTokens'

import { CartDeliveryFreeSlotsBadge } from './CartDeliveryFreeSlotsBadge'

import styles from './CartDeliveryMethod.module.scss'

interface CartDeliveryMethodProps {
  deliveryMethod: PostalCodeTypes
  checked: boolean
  countUnavailableProductsInDelivery: number | null
  isFreeDelivery: boolean
}

const CartDeliveryMethod = ({
  deliveryMethod,
  checked,
  countUnavailableProductsInDelivery,
  isFreeDelivery
}: CartDeliveryMethodProps) => {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const deliverySlotsQuery = useQueryDeliverySlots(deliveryMethod.id, token)

  const hasFreeDeliverySlots = useMemo<boolean>(() => {
    const data = deliverySlotsQuery.data

    if (!data) return false
    const methodSlots = Object.values(data).flat()

    return methodSlots.some(slot => Boolean(slot?.isFreeForCart))
  }, [deliverySlotsQuery.data])

  return (
    <DeliveryMethodWrapper
      $checked={checked}
      id={deliveryMethod.id}
      value={deliveryMethod.id}
    >
      {/* If all slots are free we don't need to show the "from free" badge */}
      {hasFreeDeliverySlots && !isFreeDelivery && (
        <CartDeliveryFreeSlotsBadge />
      )}
      {Boolean(countUnavailableProductsInDelivery) && (
        <Tooltip
          title={`Część produktów (${countUnavailableProductsInDelivery}) nie jest dostępna w tej metodzie dostawy`}
          id={`deliveryMethod-${deliveryMethod.id}-tooltip`}
          placement='top'
        >
          <IconExclamationCircle
            className={styles.errorIcon}
            color={cssColors.system.error.text}
            size={15}
          />
        </Tooltip>
      )}
      <div className={styles.nameWrapper}>
        {deliveryMethod.image ? (
          <div className={styles.imageContainer}>
            <Image
              src={deliveryMethod.image}
              alt={deliveryMethod.name}
              fill
              objectFit='contain'
            />
          </div>
        ) : (
          <div className={styles.nameWrapper}>{deliveryMethod.name}</div>
        )}
      </div>
      <div className={styles.price}>
        <div
          dangerouslySetInnerHTML={{
            __html: isFreeDelivery
              ? 'za darmo'
              : deliveryMethod?.priceGross?.formatted
          }}
        />
      </div>
    </DeliveryMethodWrapper>
  )
}

export default CartDeliveryMethod

const DeliveryMethodWrapper = ({
  $checked,
   
  $hasFreeDeliverySlots,
  className,
  ...props
}: ComponentProps<typeof RadioItem> & {
  $checked: boolean
  $hasFreeDeliverySlots?: boolean
}) => (
  <RadioItem
    {...props}
    className={clsx(
      styles.deliveryMethodWrapper,
      { [styles.unchecked]: !$checked },
      className
    )}
  />
)
